import { Fragment } from "react";
import { F, localDate, localTime, mediaKind, type Row } from "./types";
import { bpmColor, bubbleMax, dayLabel, gapBefore, gapLabel, isDayStart,
         isRunEnd, lagLabel, placeholder } from "./rows";
import { splitHighlight } from "./search";
import { Media } from "./Media";
import { Waveform } from "./Waveform";

/** Which parts of this row's body a search matched, and whether this is the one
 *  the arrows are currently standing on. */
export interface Highlight {
  ranges: readonly (readonly [number, number])[];
  active: boolean;
}

/** One row of the archive.
 *
 *  Three sides, not two. `me` and `them` are the conversation; `orphan` is a
 *  file with no message behind it -- 3,809 of the full export's 5,334, because
 *  Combined Media also holds Memories and saved snaps that never appear in a
 *  chat. An orphan has no sender at all, so it is centred and uncaptioned:
 *  putting it on the left would attribute your own camera roll to Alex.
 *
 *  The heart rate sits on the bubble's OUTER edge, away from the text, so a
 *  column of readings is scannable on its own as you flick past.
 */
export function Bubble({ rows, index, vw, vh, highlight, onOpenMedia }: {
  rows: Row[];
  index: number;
  vw: number;
  vh: number;
  highlight?: Highlight;
  onOpenMedia?: (index: number, el: HTMLElement) => void;
}) {
  const r = rows[index];
  const side = !r[F.Sender] ? "orphan" : r[F.IsMe] === 1 ? "me" : "them";
  const kind = mediaKind(r);
  const colour = bpmColor(r[F.Bpm]);
  const gap = gapBefore(rows, index);

  // The lag hangs off the reading, never stands alone: a bare '+3m' beside no
  // number is a caption for something that is not on screen. `colour` already
  // gates on bpm, so the nesting is the guarantee.
  const lag = colour ? lagLabel(r[F.BpmLagS]) : "";
  const badge = colour && (
    <span className="bpm" style={{ color: colour }}>
      &#9829; {r[F.Bpm]}
      {lag && <span className="lag">{lag}</span>}
    </span>
  );

  return (
    <div className="row" data-side={side}>
      {/* Never both. The day rule carries the break with a date on it; the gap
          chip is for a silence INSIDE one day, which nothing else would show. */}
      {isDayStart(rows, index)
        ? <div className="dayrule">{dayLabel(localDate(r))}</div>
        : gap > 0 ? <div className="gaprule">{gapLabel(gap)}</div> : null}
      <div className="line">
        {side === "me" && badge}
        <div className="bubble" style={{ maxWidth: bubbleMax(vw) }}>
          {kind === "audio" ? <Waveform row={r} />
            : kind ? <Media row={r} vw={vw} vh={vh}
                            onOpen={(el) => onOpenMedia?.(index, el)} />
            : null}
          {/* The word only appears when there is nothing to draw: a caption
              reading "photo" under an actual photo is noise, but a MEDIA
              message whose file never matched still has to say something. */}
          {r[F.Body] !== null ? (
            <span className="body"><Marked text={r[F.Body]} highlight={highlight} /></span>
          ) : kind === null ? (
            <span className="body ghost">{placeholder(r)}</span>
          ) : null}
        </div>
        {side !== "me" && badge}
      </div>
      {/* Once per run, not once per message: ten messages from one person in one
          minute is one thought, and ten captions would bury it. */}
      {isRunEnd(rows, index) && (
        <div className="meta">
          {localTime(r)}{r[F.Sender] ? ` · ${r[F.Sender]}` : ""}
        </div>
      )}
    </div>
  );
}

/** The body, with the matched runs wrapped.
 *
 *  splitHighlight guarantees the segments rejoin to the original string, so
 *  this cannot drop or duplicate a word however the ranges arrive. <mark> is
 *  inline and the text is the same length either way, which is why typing in
 *  the search box does not change a single reserved height.
 */
function Marked({ text, highlight }: { text: string; highlight?: Highlight }) {
  if (!highlight || highlight.ranges.length === 0) return <>{text}</>;
  return (
    <>
      {splitHighlight(text, highlight.ranges).map((seg, i) =>
        seg.hit
          ? <mark key={i} className={highlight.active ? "on" : undefined}>{seg.text}</mark>
          : <Fragment key={i}>{seg.text}</Fragment>,
      )}
    </>
  );
}
